import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { apiClient } from 'Helpers/network';

import useApiRequest from 'Hooks/useApiRequest';
import { useToast } from 'Hooks/useToast';

/**
 * Project Plan report builder context (IB-671).
 *
 * Mirrors the Budgets-by-Dimension report context: it holds the criteria,
 * auto-fetches the on-screen JSON whenever the criteria change, and drives the
 * generate → poll → download flow for the async Excel export.
 *
 * Contract (set by the Blade builder page):
 *   fetchReportUrl     GET  — on-screen data; criteria sent under criteria[...]
 *                            → { data: { rows, styles, summary } }
 *   exportReportUrl    POST — queues the Excel export (criteria at top level)
 *                            → { success, report: { id, ... } }
 *   downloadReportUrl  GET  — contains REPORT_CENTRE_EXPORT_ID placeholder
 *   checkStatusUrl     GET  — contains REPORT_CENTRE_EXPORT_ID placeholder
 *                            → { item: { statusKey, downloadUrl, ... }, url }
 *   scopeEntityOptions      — entity picker options keyed by scope level
 *                            ({ department, costcentre, venue }), limited to the
 *                            entities the user may see (AC#1)
 *   categoryOptions         — project category tree (root + sub) for the picker
 *   channelName             — private Echo channel broadcasting `report.generated`
 */

const DEFAULT_CRITERIA = {
    scope_level: 'central',
    scope_entity_ids: [],
    category_ids: [],
    statuses: [],
    types: [],
    owner_ids: [],
    tagged_position_ids: [],
    date_field: 'target',
    date_from: null,
    date_to: null,
    exceptions_only: false,
    group_by: 'category',
    detail_level: 'detailed',
    time_view: 'months',
    visible_columns: null,
    sort: null,
    view_mode: 'table',
};

// How often we poll the export status endpoint while an export is generating.
const POLL_INTERVAL_MS = 4000;

const ProjectPlanReportContext = createContext();

export const useProjectPlanReportContext = () => {
    const context = useContext(ProjectPlanReportContext);

    if (!context) {
        throw new Error('useProjectPlanReportContext must be used within a ProjectPlanReportProvider');
    }

    return context;
};

export const ProjectPlanReportProvider = ({
    fetchReportUrl,
    exportReportUrl,
    downloadReportUrl,
    checkStatusUrl,
    scopeOptions,
    scopeEntityOptions,
    categoryOptions,
    ownerOptions,
    positionOptions,
    statusOptions,
    typeOptions,
    channelName,
    initialCriteria,
    // Delivery config (IB-673 phase 6): the report key + endpoints and options
    // the header's Email Now / Schedule / PDF actions operate with.
    reportKey = null,
    supportedFormats = ['xlsx'],
    recipientOptions = [],
    emailNowUrl = null,
    scheduleStoreUrl = null,
    schedulePreviewUrl = null,
    scheduleDefaultTimezone = 'UTC',
    children,
}) => {
    // Seed the criteria from any pre-fill override (IB-672 — a template preset or
    // a saved set's parameters), spread over the defaults so unspecified fields
    // keep their default value.
    const [criteria, setCriteria] = useState({ ...DEFAULT_CRITERIA, ...(initialCriteria || {}) });
    const [exporting, setExporting] = useState(false);

    const toast = useToast();

    const reportRequest = useApiRequest({ url: fetchReportUrl });

    const pollTimer = useRef(null);
    // Guards against the interval poll and the Echo listener both completing
    // simultaneously and each calling startDownload → window.open before the
    // other has had a chance to clear state.
    const hasDownloaded = useRef(false);

    const options = useMemo(
        () => ({
            scope: scopeOptions || [],
            // Entity options keyed by scope level ({ department, costcentre, venue }).
            // The criteria form indexes this by the LIVE (Formik) scope level rather
            // than the committed criteria, so the picker populates the moment the
            // scope changes — not only after the form is applied.
            scopeEntitiesByLevel: scopeEntityOptions || {},
            category: categoryOptions || [],
            owner: ownerOptions || [],
            position: positionOptions || [],
            // statusOptions / typeOptions arrive as { value: label } maps from the
            // enum ::labels() helpers — normalise them to { value, label } arrays.
            status: normaliseMap(statusOptions),
            type: normaliseMap(typeOptions),
        }),
        [scopeOptions, scopeEntityOptions, categoryOptions, ownerOptions, positionOptions, statusOptions, typeOptions],
    );

    const fetchReport = useCallback(
        (nextCriteria) => {
            const { reset, get } = reportRequest;

            reset();

            // Nest the criteria so the backend reads them from input('criteria').
            return get({ criteria: serializeCriteria(nextCriteria) });
        },
        [reportRequest.reset, reportRequest.get],
    );

    // Re-fetch whenever the criteria change. The first render fetches too, so
    // the table is populated as soon as the builder loads.
    useEffect(() => {
        fetchReport(criteria);
    }, [criteria]);

    const updateCriteria = useCallback((partial) => {
        setCriteria((prev) => ({ ...prev, ...partial }));
    }, []);

    const clearPolling = useCallback(() => {
        if (pollTimer.current) {
            clearInterval(pollTimer.current);
            pollTimer.current = null;
        }
    }, []);

    const startDownload = useCallback(
        (reportId) => {
            // Guard: the interval poll and the Echo listener can both fire while
            // status === 'completed'. Only open the tab once.
            if (hasDownloaded.current) {
                return;
            }
            hasDownloaded.current = true;
            const url = downloadReportUrl.replace('REPORT_CENTRE_EXPORT_ID', reportId);
            window.open(url, '_blank');
        },
        [downloadReportUrl],
    );

    const pollStatus = useCallback(
        (reportId) => {
            const url = checkStatusUrl.replace('REPORT_CENTRE_EXPORT_ID', reportId);

            return apiClient
                .get(url)
                .then((response) => {
                    const statusKey = response.data?.item?.statusKey;

                    if (statusKey === 'completed') {
                        clearPolling();
                        setExporting(false);
                        startDownload(reportId);
                        toast.success($t('Your report is ready and the download has started.'));
                        return true;
                    }

                    if (statusKey === 'failed') {
                        clearPolling();
                        setExporting(false);
                        toast.error($t('Failed to generate the report. Please try again later.'));
                        return true;
                    }

                    return false;
                })
                .catch(() => false);
        },
        [checkStatusUrl, clearPolling, startDownload, toast],
    );

    const exportReport = useCallback(
        (format = 'excel') => {
            if (exporting) {
                return Promise.resolve();
            }

            setExporting(true);
            hasDownloaded.current = false;

            // The export endpoint reads `format` (excel | pdf) alongside the
            // criteria; only 'pdf' branches the generation job, so any other value
            // (or an omitted one) renders the identical Excel workbook.
            const payload = format === 'pdf' ? { ...criteria, format: 'pdf' } : criteria;

            return apiClient
                .post(exportReportUrl, payload)
                .then((response) => {
                    const reportId = response.data?.report?.id;

                    if (!reportId) {
                        throw new Error('Missing export report id');
                    }

                    toast.success($t('The request to generate a report is being processed. This may take a few minutes'));

                    clearPolling();
                    pollTimer.current = setInterval(() => pollStatus(reportId), POLL_INTERVAL_MS);

                    return reportId;
                })
                .catch((error) => {
                    setExporting(false);
                    toast.error($t('Failed to generate the report. Please try again later.'));
                    throw error;
                });
        },
        [exporting, exportReportUrl, criteria, toast, clearPolling, pollStatus],
    );

    // Listen for the broadcast so a completed export downloads even if the
    // poll timer is slow (parity with the archive sibling).
    useEffect(() => {
        if (!channelName || !window.Echo) {
            return undefined;
        }

        const channel = window.Echo.private(channelName);

        channel.listen('.report.generated', (event) => {
            const reportId = event?.report?.id;

            if (reportId) {
                pollStatus(reportId);
            }
        });

        return () => {
            window.Echo.leave(channelName);
        };
    }, [channelName, pollStatus]);

    // Tidy the poll timer on unmount.
    useEffect(() => clearPolling, [clearPolling]);

    const value = {
        criteria,
        options,
        updateCriteria,
        exportReport,
        exporting,
        report: reportRequest.data?.data ?? null,
        loading: reportRequest.loading,
        // Delivery config for the header actions (Email Now / Schedule / PDF).
        reportKey,
        supportedFormats,
        recipientOptions,
        emailNowUrl,
        scheduleStoreUrl,
        schedulePreviewUrl,
        scheduleDefaultTimezone,
    };

    return <ProjectPlanReportContext.Provider value={value}>{children}</ProjectPlanReportContext.Provider>;
};

/**
 * Serialise criteria for the GET query. A JS boolean becomes the string
 * "true"/"false" in a query string, which Laravel's `boolean` validation rule
 * rejects (it accepts only 1/0/"1"/"0"). Send booleans as 1/0 so a criterion
 * like the "Exceptions only" toggle validates instead of 422-ing the fetch.
 */
const serializeCriteria = (criteria) =>
    Object.fromEntries(Object.entries(criteria || {}).map(([key, value]) => [key, typeof value === 'boolean' ? Number(value) : value]));

/**
 * Normalise a { value: label } map (from an enum ::labels() helper) into an
 * array of { value, label } option objects the select controls understand.
 * Pass-through when an array of options is already supplied.
 */
const normaliseMap = (input) => {
    if (Array.isArray(input)) {
        return input;
    }

    if (!input || typeof input !== 'object') {
        return [];
    }

    return Object.entries(input).map(([value, label]) => ({ value, label }));
};

export default ProjectPlanReportContext;
